import { useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, Lock, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Settings = ({ onLogout }) => {
  const navigate = useNavigate();
  const username = localStorage.getItem('username');
  const userRole = localStorage.getItem('role');
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newUsername: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.currentPassword) {
      toast.error('Current password is required');
      return;
    }

    if (!formData.newUsername && !formData.newPassword) {
      toast.error('Please provide a new username or password to update');
      return;
    }

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.newPassword && formData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        current_password: formData.currentPassword
      };

      if (formData.newUsername && formData.newUsername !== username) {
        updateData.new_username = formData.newUsername;
      }

      if (formData.newPassword) {
        updateData.new_password = formData.newPassword;
      }

      const response = await axiosInstance.put('/users/profile', updateData);

      // Update local storage with new credentials
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('username', response.data.username);
      localStorage.setItem('role', response.data.role);

      toast.success('Profile updated successfully!');
      
      // Clear form
      setFormData({
        currentPassword: '',
        newUsername: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Navigate back to dashboard
      setTimeout(() => {
        navigate('/');
      }, 1500);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-blue-50" data-testid="settings-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Button
              data-testid="back-button"
              onClick={() => navigate('/')}
              variant="outline"
              className="border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Button
              data-testid="logout-button"
              onClick={onLogout}
              variant="outline"
              className="border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800">Account Settings</h1>
          <p className="text-slate-600 mt-2">
            Update your username and password
          </p>
        </div>

        <Card className="shadow-xl border-0 bg-white/90">
          <CardHeader>
            <CardTitle className="text-2xl">Profile Information</CardTitle>
            <CardDescription>
              Current username: <span className="font-semibold text-slate-700">{username}</span>
              {userRole === 'owner' && <span className="ml-2 text-blue-600 font-semibold">(Owner)</span>}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Current Password */}
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="flex items-center">
                  <Lock className="w-4 h-4 mr-2" />
                  Current Password *
                </Label>
                <Input
                  id="currentPassword"
                  data-testid="current-password-input"
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  required
                  placeholder="Enter your current password"
                  className="border-slate-300"
                />
                <p className="text-xs text-slate-500">Required to make any changes</p>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-700 mb-4">Change Username</h3>
                
                {/* New Username */}
                <div className="space-y-2">
                  <Label htmlFor="newUsername" className="flex items-center">
                    <UserIcon className="w-4 h-4 mr-2" />
                    New Username
                  </Label>
                  <Input
                    id="newUsername"
                    data-testid="new-username-input"
                    type="text"
                    value={formData.newUsername}
                    onChange={(e) => setFormData({ ...formData, newUsername: e.target.value })}
                    placeholder="Enter new username (optional)"
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">Leave blank to keep current username</p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-700 mb-4">Change Password</h3>
                
                {/* New Password */}
                <div className="space-y-2 mb-4">
                  <Label htmlFor="newPassword" className="flex items-center">
                    <Lock className="w-4 h-4 mr-2" />
                    New Password
                  </Label>
                  <Input
                    id="newPassword"
                    data-testid="new-password-input"
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    placeholder="Enter new password (optional)"
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">Minimum 6 characters. Leave blank to keep current password</p>
                </div>

                {/* Confirm Password */}
                {formData.newPassword && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="flex items-center">
                      <Lock className="w-4 h-4 mr-2" />
                      Confirm New Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      data-testid="confirm-password-input"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      className="border-slate-300"
                    />
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <Button
                  type="submit"
                  data-testid="save-changes-button"
                  className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-medium py-3"
                  disabled={loading}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving Changes...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">ℹ</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900 mb-1">Important Information</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• You must enter your current password to make any changes</li>
                  <li>• Changing your username will update it on all jobs you've created</li>
                  <li>• You'll be logged in with your new credentials automatically</li>
                  <li>• Make sure to remember your new username and password</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Settings;
